import { Injectable } from '@angular/core';
import { IScheduledCampaign } from '../../interfaces/scheduled-campaign.interface';

/**
 * Servicio para gestionar el estado y cálculos de campañas
 * Maneja auto-refresh, métricas agregadas y progreso visual
 */
@Injectable({
  providedIn: 'root'
})
export class CampaignService {
  selectedCampaign: IScheduledCampaign | null = null;
  showDetail = false;
  autoRefreshEnabled = true;
  refreshInterval = 30000; // 30 segundos
  private refreshTimer: any;

  constructor() {}

  /**
   * Inicia el auto-refresh con un callback
   */
  startAutoRefresh(callback: () => void): void {
    if (this.autoRefreshEnabled) {
      this.refreshTimer = setInterval(() => {
        callback();
      }, this.refreshInterval);
    }
  }

  /**
   * Detiene el auto-refresh
   */
  stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Alterna el auto-refresh on/off
   */
  toggleAutoRefresh(callback: () => void): void {
    this.autoRefreshEnabled = !this.autoRefreshEnabled;
    
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh(callback);
    } else {
      this.stopAutoRefresh();
    }
  }

  /**
   * Calcula el porcentaje de progreso de una campaña
   */
  calculateProgress(campaign: IScheduledCampaign): number {
    if (!campaign.results || campaign.results.length === 0) {
      return 0;
    }
    
    const totalChannels = campaign.results.length;
    const completedChannels = campaign.results.filter(
      result => result.success || result.error
    ).length;
    
    return Math.round((completedChannels / totalChannels) * 100);
  }

  /**
   * Obtiene el texto de resultado por canal para mostrar en tabla
   * Muestra iconos por canal con su estado (✅ éxito, ❌ error)
   * @example "📧✅ 💬❌ 📱✅" = Email OK, SMS falló, WhatsApp OK
   */
  getChannelResultsDisplay(campaign: IScheduledCampaign): string {
    if (!campaign.results || campaign.results.length === 0) {
      return '⏳ Sin ejecutar';
    }
    
    const icons: { [key: string]: string } = {
      'EMAIL': '📧',
      'SMS': '💬',
      'WHATSAPP': '📱',
      'CALL': '📞'
    };
    
    return campaign.results.map(result => {
      const icon = icons[result.channel] || '📌';
      const status = result.success ? '✅' : '❌';
      return `${icon}${status}`;
    }).join(' ');
  }

  /**
   * Agrega métricas de múltiples campañas
   */
  aggregateMetrics(campaigns: IScheduledCampaign[]): any {
    const totals = {
      total: campaigns.length,
      pending: 0,
      completed: 0,
      failed: 0,
      cancelled: 0,
      totalSent: 0,
      successful: 0,
      failedSends: 0,
      pendingSends: 0
    };
    
    campaigns.forEach(campaign => {
      // Contar por estado
      switch (campaign.status) {
        case 'PENDING':
          totals.pending++;
          break;
        case 'COMPLETED':
          totals.completed++;
          break;
        case 'FAILED':
          totals.failed++;
          break;
        case 'CANCELLED':
          totals.cancelled++;
          break;
      }
      
      // Agregar métricas si existen
      if (campaign.metrics) {
        totals.totalSent += campaign.metrics.totalSent || 0;
        totals.successful += campaign.metrics.successful || 0;
        totals.failedSends += campaign.metrics.failed || 0;
        totals.pendingSends += campaign.metrics.pending || 0;
      }
    });
    
    return totals;
  }
}
